import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance as axios } from '../../../context/AuthContext';
import { toast } from 'sonner';
import { RequestCard } from './RequestCard';
import { FulfillDialog } from './FulfillDialog';
import { RequestsMap } from './RequestsMap';
import { Map as MapIcon } from 'lucide-react';

const FILTERS = [['active', 'Waiting'], ['fulfilled', 'Fulfilled'], ['dismissed', 'Dismissed'], ['all', 'All']];

export default function FamilyRequestsTab({ onChanged }) {
  const [status, setStatus] = useState('active');
  const [requests, setRequests] = useState(null);
  const [fulfilling, setFulfilling] = useState(null);
  const [showMap, setShowMap] = useState(false);

  const load = useCallback(() => {
    setRequests(null);
    axios.get(`/api/professional-requests/admin?status=${status}`)
      .then(r => setRequests(r.data))
      .catch(() => { setRequests([]); toast.error('Could not load family requests'); });
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (req, action, body) => {
    if (action === 'fulfill' && !body) { setFulfilling(req); return; }
    if (action === 'dismiss' && !window.confirm(`Dismiss the request from ${req.user_name}?`)) return;
    try {
      await axios.post(`/api/professional-requests/${req.request_id}/${action}`, body || {});
      toast.success(action === 'fulfill' ? 'Fulfilled — the family has been notified' : 'Request dismissed');
      setFulfilling(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="family-requests-tab">
      <p className="text-slate-400 text-sm">
        Families who asked for a professional that isn't listed yet. Check who asked, recruit a match from the Recruit tab, then mark it fulfilled.
      </p>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setStatus(key)}
            className={`px-3 py-1.5 rounded-full text-sm ${status === key ? 'bg-amber-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
            data-testid={`requests-filter-${key}`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => setShowMap(m => !m)}
          className={`ml-auto px-3 py-1.5 rounded-full text-sm flex items-center gap-1 ${showMap ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
          data-testid="requests-map-toggle"
        >
          <MapIcon className="h-4 w-4" />{showMap ? 'Hide map' : 'Show map'}
        </button>
      </div>
      {showMap && <RequestsMap refreshKey={requests?.length} />}
      {requests === null && <p className="text-slate-400">Loading…</p>}
      {requests?.length === 0 && <p className="text-slate-400 py-8 text-center" data-testid="requests-empty">No requests here.</p>}
      {requests?.map(r => <RequestCard key={r.request_id} req={r} onAct={act} />)}
      <FulfillDialog req={fulfilling} onClose={() => setFulfilling(null)} onConfirm={(r, body) => act(r, 'fulfill', body)} />
    </div>
  );
}
